<template>
  <var-dialog
    v-model:show="visible"
    :title="dialogTitle"
    :confirm-button-text="confirmText"
    :cancel-button-text="cancelText"
    :show-confirm-button="showConfirmButton"
    :show-cancel-button="showCancelButton"
    @confirm="onConfirmAnother"
    @cancel="onCancel"
    @close="onClose"
  >
    <div
      v-if="visible"
      style="height: 100%;"
    >
      <DYPAGE
        v-if="!isLightMode"
        :pconfig="dialogPageConfig"
        :paramFormData="paramFormData"
        :idx="idx"
        mitttype="dialog"
      />
      <slot v-else />
    </div>
  </var-dialog>
</template>

<script setup lang="ts">
import '@varlet/ui/es/dialog/style/index';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import mitt from 'mitt-utils';
import { generateToast, isJsonV } from '../../utils/tools';
import { exeAction, getPid } from 'exeAction-utils';
import DYPAGE from './v_content.vue';

onMounted(() => {
  console.log('dialog.vue 组件已挂载');
});

let frontId: number = new Date().getTime();
const route = useRoute();
const router = useRouter();
let pid = getPid(route);

const props = defineProps({
  config: {
    type: Object,
    default: () => ({})
  },
  show: {
    type: Boolean,
    default: undefined
  },
  title: {
    type: String,
    default: undefined
  },
  showConfirmButton: {
    type: Boolean,
    default: undefined
  },
  showCancelButton: {
    type: Boolean,
    default: undefined
  },
  confirmButtonText: {
    type: String,
    default: undefined
  },
  cancelButtonText: {
    type: String,
    default: undefined
  }
});
const emit = defineEmits(['update:show', 'confirm', 'cancel', 'close']);

const conf = props.config;
// 与 tdesign_dialog 对齐的结构解构
const {
  idx = undefined,
  body = [],
  drag = true,
  close = {},
  css = {},
  firm = {},
  cancle = {}
} = props.config;

const n = `h5_dialog > `;
// 双模式约定：
// 1) 传入有效 config => 旧模式（mitt 驱动 + DYPAGE）
// 2) config 为空对象/未传 => 轻量模式（v-model:show + title + slot）
const isLightMode = computed(() => {
  const c = props.config as any;
  return !isJsonV(c) || Object.keys(c).length === 0;
});

// 是否显示弹框
const configVisible = ref(false);
// visible 统一对外给 var-dialog 使用：
// - 轻量模式：走 props.show / emit('update:show')
// - 旧模式：走内部状态 configVisible（由 mitt 事件控制）
const visible = computed({
  get() {
    if (isLightMode.value) return !!props.show;
    return configVisible.value;
  },
  set(v: boolean) {
    if (isLightMode.value) {
      emit('update:show', v);
    } else {
      configVisible.value = v;
    }
  }
});

// 存储数据和弹框内部页面配置
const paramFormData = ref<any>({});
const dialogPageConfig = ref<any>({
  contents: body,
  dialogs: [] 
});

// 关闭弹框
const closeDialog = () => {
  visible.value = false;
};

const getFootBtn = (idx: number) => {
  const foot = (conf as any)?.foot;
  const divs = foot?.attr?.divs;
  if (!Array.isArray(divs)) return undefined;
  return divs[idx];
};

const getFootBtnAction = (idx: number) => {
  return getFootBtn(idx)?.click;
};

const confirmText = computed(() => {
  // 轻量模式优先读显式 props；旧模式沿用历史 config 解析规则
  if (isLightMode.value) return props.confirmButtonText || '确定';
  const ft = conf?.cfirm;
  if (ft !== undefined && ft !== null && ft !== false) return ft;
  const txt = getFootBtn(0)?.attr?.b_c;
  return txt || '确定';
});

const cancelText = computed(() => {
  if (isLightMode.value) return props.cancelButtonText || '取消';
  const ft = conf?.ccancel;
  if (ft !== undefined && ft !== null && ft !== false) return ft;
  const txt = getFootBtn(1)?.attr?.b_c;
  return txt || '取消';
});

const showConfirmButton = computed(() => {
  if (isLightMode.value) return props.showConfirmButton !== false;
  return conf.cfirm !== false;
});

const showCancelButton = computed(() => {
  if (isLightMode.value) return props.showCancelButton !== false;
  return conf.ccancel !== false;
});

const dialogTitle = computed(() => {
  if (isLightMode.value) return props.title || '提示';
  return conf['t'] || '提示';
});

// 关闭事件处理
const onClose = () => {
  emit('close');
  // 轻量模式只负责关闭和事件透出，不执行 action
  if (isLightMode.value) {
    closeDialog();
    return;
  }
  console.log(`${n}关闭事件触发`);
  if (!isJsonV(close)) {
    closeDialog();
  } else {
    exeAction(router, close, paramFormData.value, pid, frontId);
  }
};

// 取消事件处理
const onCancel = () => {
  emit('cancel');
  // 轻量模式只负责关闭和事件透出，不执行 action
  if (isLightMode.value) {
    closeDialog();
    return;
  }
  console.log(`${n}取消事件触发`);
  const act = isJsonV(cancle) ? cancle : getFootBtnAction(1);
  if (!isJsonV(act)) {
    closeDialog();
    return;
  }
  exeAction(router, act, paramFormData.value, pid, frontId);
};

// 确定事件处理
const onConfirmAnother = () => {
  emit('confirm');
  // 轻量模式只负责关闭和事件透出，不执行 action
  if (isLightMode.value) {
    closeDialog();
    return;
  }
  console.log(`${n}确认事件触发`);
  const act = isJsonV(firm) ? firm : getFootBtnAction(0);
  if (!isJsonV(act)) {
    closeDialog();
    return;
  }
  exeAction(router, act, paramFormData.value, pid, frontId);
};

/** ***************** 监听********* */
const mittName = `${pid}_mitt_did_${idx}`;
// 仅旧模式注册 mitt，避免轻量模式误监听业务事件
if (!isLightMode.value && isJsonV(conf) && idx !== undefined) {
  mitt.on(mittName, (par: any = {}) => {
    console.log(`${n}收到事件:`, par.flg);
    if (par.flg === 'open' || par.flg === 'f_open') {
      if (par.preRes) {
        paramFormData.value = par.preRes;
      } else if (par.res) {
        paramFormData.value = par.res;
      }
      if (par.ks) {
        const conts = dialogPageConfig.value.contents || [];
        par.ks.forEach((itm: any) => {
          const cidx = conts.findIndex((its: any) => its.idx === itm);
          if (cidx !== -1) conts[cidx].v = true;
        });
      }
      visible.value = true;
    } else if (par.flg === 'f_close') {
      visible.value = false;
    } else if (par.flg === 'send_parms') {
      paramFormData.value = par.res;
    }
  });
}

// 错误界面item
mitt.on(`apiToast_${frontId}`, (par: any) => {
  generateToast(par);
});

onBeforeUnmount(() => {
  closeDialog();
  if (!isLightMode.value && isJsonV(conf) && idx !== undefined) {
    mitt.off(mittName);
  }
  mitt.off(`apiToast_${frontId}`);
});

console.log(`${n}加载完成`);
</script>

<style lang="less" scoped>
/* 让标题居中 */
// :global(.var-dialog__title) {
//   text-align: center !important;
//   justify-content: center !important;
//   width: 100% !important;
// }

// :global(.var-dialog__header) {
//   display: flex !important;
//   justify-content: center !important;
//   align-items: center !important;
// }

/* 确保内容区域正确显示 */
:global(.var-dialog__body) {
  padding: 0 !important;
  overflow: visible !important;
  height: 100% !important;
}

:global(.var-card__footer) {
  margin: 0 !important;
}
</style>